import classNames from 'classnames';
import { SkeletonLine } from '@crazyegginc/hatch';

import { usePermissions } from '/src/hooks';
import { FEATURES } from '/src/features/_global/constants';
import { WebP } from '/src/components/WebP';
import { GoalTriggersTooltip } from '/src/features/goals/components/GoalTriggersTooltip';
import { getGoalTriggerMetadata } from '/src/features/goals/triggerMetadata';
import { getGoalTriggerType } from '/src/features/goals/utils';

import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-circle-filled.svg';

export function GoalCard({
  goal,
  pending = false,
  selected = false,
  onSelect = null,
  onEdit = null,
  onDelete = null,
  isDraft = false,
  showUsageCount = true,
}) {
  const permissions = usePermissions();
  const canEditGoals = permissions.can('edit', FEATURES.GOALS).allowed;
  const goalTriggerType = getGoalTriggerType(goal);
  const goalTriggerMetadata = getGoalTriggerMetadata(goalTriggerType);
  const hasTriggers = Array.isArray(goal?.triggers) && goal.triggers.length > 0;
  const shouldShowUsageCount = !isDraft && showUsageCount;

  return (
    <div
      data-testid="goal-card"
      role={onSelect ? 'button' : undefined}
      aria-label={goal.name}
      tabIndex={onSelect ? 0 : undefined}
      className={classNames(
        'group relative flex w-full min-w-0 rounded border bg-white px-4 py-5 transition-transform duration-200 ease-in-out',
        {
          'hover:scale-105 hover:shadow-md': !!onSelect,
          'scale-105 border-dodger-blue-500 shadow-md': selected,
          'border-mystic-500': !selected,
          'cursor-pointer': !!onSelect,
        },
      )}
      onClick={() => {
        if (!selected) {
          onSelect?.(goal);
        } else {
          onSelect?.(null);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onSelect?.(goal);
        }
      }}
    >
      <div className="mr-5 flex w-[35px] items-center">
        <WebP
          className="w-[35px]"
          webp={goalTriggerMetadata.webp}
          fallback={goalTriggerMetadata.png}
          width="35"
          height="35"
          alt="goal"
        />
      </div>
      <div className="flex-1 flex-col items-start overflow-hidden text-left">
        <div className="mb-1 text-sm font-semibold truncate">{goal.name.length ? goal.name : '[Unnamed goal]'}</div>

        {hasTriggers ? (
          <div className="leading-snug flex">
            <GoalTriggersTooltip triggers={goal.triggers}>
              <span className="text-2xs text-lynch-500 border-b border-dashed border-b-cadet-blue-500">
                View triggers
              </span>
            </GoalTriggersTooltip>
          </div>
        ) : null}
      </div>
      <div className="w-[175px] self-end text-2xs text-lynch-500">
        <div className="flex justify-end">
          {shouldShowUsageCount ? (
            <div className="self-end truncate leading-none">
              Used in <strong>{goal.abTestsCountActive || 0}</strong> tests
            </div>
          ) : null}
          <div
            className={classNames('invisible w-0 opacity-0 transition duration-200 ease-in-out', {
              'hover:transform group-hover:visible group-hover:flex group-hover:w-auto group-hover:opacity-100':
                pending || !!onSelect,
            })}
          >
            <div className="relative z-0 ml-4 self-end leading-none">
              {shouldShowUsageCount ? (
                <div className="absolute -left-2 bottom-0 top-[2px] m-auto h-[2px] w-[2px] rounded-full bg-lynch-500"></div>
              ) : null}
              <button type="button" className="text-2xs text-dodger-blue-500 hover:underline" onClick={onEdit}>
                Edit
              </button>
            </div>
            <div className="relative z-0 ml-4 self-end leading-none">
              <div className="absolute -left-2 bottom-0 top-[2px] m-auto h-[2px] w-[2px] rounded-full bg-lynch-500"></div>
              {isDraft || pending || canEditGoals ? (
                <button type="button" className="text-2xs text-carnation-500 hover:underline" onClick={onDelete}>
                  Delete
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {selected ? (
        <TickIcon
          className="absolute right-2.5 top-2.5 h-5 w-5 fill-current text-dodger-blue-500"
          aria-label="selected"
        />
      ) : null}
    </div>
  );
}

export function GoalRequired({ onClick = null }) {
  const noGoalTriggerType = getGoalTriggerType(null);
  const noGoalMetadata = getGoalTriggerMetadata(noGoalTriggerType);

  return (
    <div
      role="button"
      aria-label="Goal required"
      tabIndex={0}
      className="group relative flex w-full min-w-0 cursor-pointer rounded border border-carnation-500 bg-white px-4 py-5 transition-transform duration-200 ease-in-out"
      onClick={() => {
        onClick?.();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onClick?.();
        }
      }}
    >
      <div className="mr-5 flex w-[35px] items-center">
        <WebP
          className="w-[35px]"
          webp={noGoalMetadata.webp}
          fallback={noGoalMetadata.png}
          width="35"
          height="35"
          alt="goal"
        />
      </div>
      <div className="flex-1 flex-col items-start overflow-hidden text-left">
        <div className="mb-1 text-sm font-semibold text-carnation-500">No goal selected</div>
        <p className="text-body-4">Click to select a new Goal</p>
      </div>
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div className="group relative flex w-full min-w-0 cursor-pointer rounded border border-mystic-500 bg-white px-4 py-5 transition-transform duration-200 ease-in-out">
      <div className="mr-5 flex w-[35px] items-start">
        <SkeletonLine className="!h-[35px] !rounded" width="100%" />
      </div>
      <div className="flex-1 flex-col items-start space-y-2 overflow-hidden text-left">
        <div className="mb-1 text-sm font-semibold">
          <SkeletonLine />
        </div>
        <div className="flex flex-col space-y-2">
          <SkeletonLine width="40%" />
          <SkeletonLine width="40%" />
        </div>
      </div>
      <div className="w-[175px] self-end text-2xs text-lynch-500">
        <div className="flex justify-end">
          <div className="self-end truncate leading-none">
            <SkeletonLine width="100px" />
          </div>
        </div>
      </div>
    </div>
  );
}
